import {DialogTitle} from '@apps-common/ui-components';
import {Polygon as PolygonIcon} from '@apps-common/ui-icons';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LoopIcon from '@mui/icons-material/Loop';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  useColorScheme,
} from '@mui/material';
import {useQueryClient} from '@tanstack/react-query';
import numeral from 'numeral';
import pThrottle from 'p-throttle';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';
import {ErrorCode} from 'react-dropzone';
import DropZone from './Uploads/DropZone.jsx';
import FileGroup from './Uploads/FileGroup.jsx';
import {ingestFiles, queryKeys, uploadToGCS} from '@api/asyncIngest';
import {trackEvent} from '@app/analytics';
import {MAX_ASYNC_UPLOADS_SIZE_BYTES} from '@app/config';

const throttle = pThrottle({
  limit: 4,
  interval: 1000,
});

// file extensions supported by the async ingest endpoint and their content types.
// this is used by dropzone to control the supported file types in the file dialog.
const SUPPORTED_CONTENT_TYPES = {
  'application/json': ['.geojson', '.json'],
  'application/octet-stream': ['.shp', '.shx', '.gpx'],
  'application/x-dbf': ['.dbf'],
  'application/text': ['.prj', '.cpg'],
  'application/vnd.google-earth.kml+xml': ['.kml'],
  'application/vnd.google-earth.kmz': ['.kmz'],
  'application/geopackage+sqlite3': ['.gpkg'],
};

const SHAPEFILE_EXTENSIONS = ['shp', 'shx', 'dbf', 'prj', 'cpg'];

// Map DropZone error codes to our own messages
const REJECTED_REASONS = {
  [ErrorCode.FileInvalidType]: 'unsupported file extension',
  [ErrorCode.FileTooLarge]: `file is larger than ${numeral(MAX_ASYNC_UPLOADS_SIZE_BYTES).format('0b')}`,
};

// steps in the async upload process
const STEP = {
  UPLOAD: 'upload',
  PROGRESS: 'progress',
};

const getExtension = path => path.toLowerCase().split('.').pop();

const fileGroupIsValid = group => {
  if (group.isShapefile) {
    return !!group.files.find(file => getExtension(file.path) === 'shp');
  }

  return true;
};

const groupFiles = ({incoming, existing = []}) => {
  // combine existing and incoming files
  const allFiles = [...existing.map(group => group.files).flat(), ...incoming];

  const groupIdx = {};
  const groups = [];
  const filesSeen = {};

  allFiles.forEach(file => {
    if (filesSeen[file.name]) {
      // duplicate
      return;
    }

    const parts = file.name.split('.');
    const ext = parts.pop();
    let isShapefile = false;
    let groupName;

    // Create a name for the group.
    // If the file is a shapefile, group all the shapefile parts together.
    if (SHAPEFILE_EXTENSIONS.includes(ext)) {
      groupName = parts.join('.') + '.shp';
      isShapefile = true;
    } else {
      groupName = file.name;
    }

    // create the group if it doesn't exist
    if (groupIdx[groupName] === undefined) {
      groupIdx[groupName] = groups.length;
      groups.push({name: groupName, files: [], isShapefile});
    }

    // add the file to the group
    groups[groupIdx[groupName]].files.push(file);
    groups[groupIdx[groupName]].isValid = fileGroupIsValid(
      groups[groupIdx[groupName]]
    );

    filesSeen[file.name] = true;
  });

  return groups;
};

const transformRejections = fileRejections => {
  return fileRejections.map(rejection => ({
    name: rejection.file.name,
    extension: getExtension(rejection.file.name),
    size: rejection.file.size,
    errors: rejection.errors,
    errorMessages: rejection.errors.map(error => REJECTED_REASONS[error.code]),
  }));
};

const AsyncUpload = ({open = true, onClose = () => {}, inFiles = {}}) => {
  const queryClient = useQueryClient();

  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(STEP.UPLOAD);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const [rejectedFiles, setRejectedFiles] = useState([]);

  // cancellation for the upload request
  const cancelToken = useRef(null);
  const requestCanceled = useRef(false);

  useEffect(() => {
    if (inFiles.acceptedFiles) {
      setFiles(groupFiles({incoming: inFiles.acceptedFiles}));
    }
    if (inFiles.rejectedFiles) {
      setRejectedFiles(transformRejections(inFiles.rejectedFiles));
    }
  }, [inFiles]);

  const resetState = () => {
    setFiles([]);
    setProcessing(false);
    setUploadResults([]);
    setUploadError(null);
    setUploadSuccess(false);
    setUploadProgress({});

    cancelToken.current = null;
    requestCanceled.current = false;

    setStep(STEP.UPLOAD);
  };

  const handleClose = () => {
    onClose();
    resetState();
    setRejectedFiles([]);
    trackEvent('Async Upload', {
      action: 'closed upload file modal',
    });
  };

  const handleUploadDone = () => {
    setProcessing(false);
    setRejectedFiles([]);
    queryClient.invalidateQueries({queryKey: queryKeys.lists()});
  };

  // create a new upload progress function for a given file
  // the function will set the progress state like so:
  //
  // { "group1": {"file1.shp": 50, "file2.shx": 100, ...}, "group2": ...}
  //
  // This allows the progress state to be passed to each FileGroup component
  // individually.
  const newUploadProgressFn = (group, file) => progressEvent => {
    // update progress for the current file
    const progress = Math.round(
      (progressEvent.loaded * 100) / progressEvent.total
    );
    setUploadProgress(cur => ({
      ...cur,
      [group.name]: {...cur[group.name], [file.name]: progress},
    }));
  };

  // handle the full flow of uploading one file (or shapefile with sidecars)
  // from the initial Granero request, to uploading the file to GCS.
  const uploadFileGroup = async group => {
    return await ingestFiles(group.files).then(response =>
      Promise.all(
        response.data.urls.map((upload, i) =>
          uploadToGCS(upload.url, group.files[i], {
            onUploadProgress: newUploadProgressFn(group, group.files[i]),
            signal: cancelToken.current.signal,
          })
        )
      )
    );
  };

  const handleUpload = async () => {
    setProcessing(true);
    setStep(STEP.PROGRESS);

    const validFiles = files.filter(file => file.isValid);

    trackEvent('Async Upload', {
      action: 'clicked upload files',
      extensions: validFiles.map(file => getExtension(file.name)),
    });

    // create cancellation token for this request
    cancelToken.current = new AbortController();

    const results = await Promise.allSettled(
      validFiles.map(throttle(group => uploadFileGroup(group)))
    );

    setUploadResults(results);

    if (requestCanceled.current) {
      setUploadError('Upload canceled.');
    }

    if (results.some(result => result.status === 'fulfilled')) {
      setUploadSuccess(true);
    }
    // clear out errors after upload
    setFiles(validFiles);

    handleUploadDone();
  };

  const handleCancelUpload = () => {
    if (cancelToken.current) {
      cancelToken.current.abort();
    }
    requestCanceled.current = true;
    trackEvent('Async Upload', {
      action: 'clicked cancel upload',
    });
  };

  // add files, ensuring no duplicates
  const onAddFiles = acceptedFiles => {
    const groups = groupFiles({incoming: acceptedFiles, existing: files});
    setFiles(groups);
    trackEvent('Async Upload', {
      action: 'add upload files to dropzone',
      extensions: acceptedFiles.map(file => getExtension(file.name)),
    });
  };

  // notify on rejected files
  const onRejectedFiles = fileRejections => {
    const rejections = transformRejections(fileRejections);

    setRejectedFiles(prevRejects => {
      return [...prevRejects, ...rejections];
    });

    trackEvent('Async Upload', {
      action: 'rejected file upload',
      rejected_file_info: rejections.map(rejection => ({
        extension: rejection.extension,
        bytes: rejection.size,
      })),
    });
  };

  // remove a file from the list of files
  const onDeleteGroup = group => {
    const newFiles = files.filter(g => g.name !== group.name);
    setFiles(newFiles);
    if (newFiles.length === 0) {
      resetState();
    }
  };

  const canContinue = !!files.length && files.some(file => file.isValid);

  const {colorScheme} = useColorScheme();
  const alertVariant = colorScheme === 'dark' ? 'outlined' : 'standard';

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle
        icon={<PolygonIcon />}
        onClose={handleClose}
        title="Upload AOI"
      />

      <DialogContent
        dividers
        sx={{display: 'flex', p: 0.5, minHeight: 600, flexDirection: 'column'}}
      >
        <DropZone
          onDrop={acceptedFiles => {
            onAddFiles(acceptedFiles);
          }}
          files={files}
          acceptedContentTypes={SUPPORTED_CONTENT_TYPES}
          supportedExtensions={Object.values(SUPPORTED_CONTENT_TYPES).flat()}
          allowAdd={step === STEP.UPLOAD}
          onDropRejected={onRejectedFiles}
        >
          {files.map((group, i) => (
            <FileGroup
              key={`g-${group.name}`}
              group={group}
              progress={uploadProgress[group.name]}
              result={uploadResults ? uploadResults[i] : null}
              onDeleteGroup={onDeleteGroup}
            />
          ))}
        </DropZone>

        <Stack sx={{gap: 2, paddingInline: 2, paddingBlockEnd: 2}}>
          {!!rejectedFiles.length && (
            <Alert
              severity="error"
              variant={alertVariant}
              onClose={() => setRejectedFiles([])}
            >
              <AlertTitle>
                {`Cannot upload ${rejectedFiles.length > 1 ? 'these files' : 'this file'}`}
              </AlertTitle>

              <Box component="ul" sx={{marginBlockEnd: 1, marginBlockStart: 0}}>
                {rejectedFiles.map(file => {
                  return (
                    <li key={file.name}>
                      {file.name} — {file.errorMessages.join(', ')}
                    </li>
                  );
                })}
              </Box>
            </Alert>
          )}

          {step === STEP.PROGRESS && !processing && uploadSuccess && (
            <Alert severity="success" variant={alertVariant}>
              File{files.length > 1 ? 's' : ''} uploaded. Check the status of
              your uploads in the bottom left navigation using the{' '}
              <LoopIcon fontSize="small" sx={{marginBlockEnd: -0.5}} /> icon.
            </Alert>
          )}

          {step === STEP.PROGRESS && !processing && uploadError && (
            <Alert severity="error" variant={alertVariant}>
              {uploadError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        {step === STEP.UPLOAD && (
          <>
            <Button
              color="primary"
              variant="contained"
              disabled={!canContinue}
              onClick={handleUpload}
              endIcon={<ArrowForwardIcon />}
            >
              Continue
            </Button>
          </>
        )}

        {step === STEP.PROGRESS && (
          <>
            {processing && cancelToken && (
              <Button
                onClick={() => handleCancelUpload()}
                color="primary"
                variant="contained"
              >
                Cancel
              </Button>
            )}
            {!processing && (
              <Button
                onClick={resetState}
                color="primary"
                disabled={processing}
              >
                Upload another file
              </Button>
            )}
            <Button
              onClick={handleClose}
              color="primary"
              variant="contained"
              disabled={processing}
            >
              Close
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

AsyncUpload.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  inFiles: PropTypes.object,
};

export default AsyncUpload;
